"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button, Input } from "./ui";
import {
  platform,
  type DesignSession,
  type DesignInvitationDetails,
} from "./api";
import css from "./ManagedAuth.module.css";

import { BUILDY } from "./brand";

export function ManagedInvitationSetup({
  onLogin,
  token: suppliedToken,
}: {
  onLogin: (session: DesignSession) => void;
  token?: string;
}) {
  const [token] = useState(
    () =>
      suppliedToken ??
      (typeof window !== "undefined"
        ? (new URLSearchParams(window.location.hash.slice(1)).get("invite") ??
          "")
        : ""),
  );
  const [details, setDetails] = useState<DesignInvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [visible, setVisible] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [retry, setRetry] = useState(0);
  const lock = useRef(false);
  useEffect(() => {
    if (window.location.hash.includes("invite="))
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setCanRetry(false);
    if (!token) {
      setError("Open the invitation link from your email to set up access.");
      setLoading(false);
      return;
    }
    void platform<DesignInvitationDetails>("design/invitations/inspect/", {
      method: "POST",
      body: { token },
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setDetails(value);
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setCanRetry(
            !(
              cause instanceof Error &&
              "status" in cause &&
              [400, 401, 403, 410].includes(Number(cause.status))
            ),
          );
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not open this invitation.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, retry]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!details || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const authorization = details.needs_password_setup
        ? undefined
        : "Basic " +
          btoa(
            Array.from(
              new TextEncoder().encode(details.email + ":" + password),
              (value) => String.fromCharCode(value),
            ).join(""),
          );
      const result = await platform<DesignSession>(
        "design/invitations/accept/",
        {
          method: "POST",
          authorization,
          body: {
            token,
            accepted_legal_terms: accepted,
            legal_terms_version: details.legal_terms_version,
            ...(details.needs_password_setup
              ? { password1: password, password2: confirmation }
              : {}),
          },
        },
      );
      setPassword("");
      setConfirmation("");
      onLogin(result);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not finish account setup.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <div className={css.shell}>
      <header className={css.header}>
        <a href="/" className={css.brand}>
          <img
            className={css.brandRobot}
            src={BUILDY.robot}
            alt=""
            width={37}
            height={44}
          />
          {BUILDY.name}
        </a>
      </header>
      <main className={css.main}>
        <section
          className={css.card}
          aria-labelledby="setup-heading"
          aria-busy={loading || busy}
        >
          <h1 id="setup-heading">
            {loading
              ? "Opening your invitation"
              : details?.needs_password_setup
                ? "Set up your account"
                : "Open your website"}
          </h1>
          {loading ? (
            <p role="status">Checking your invitation…</p>
          ) : (
            details && (
              <>
                <p className={css.lede}>
                  {details.needs_password_setup
                    ? `Create your password to manage ${details.website_name}.`
                    : `Use your existing Intelliminds or Listy password to manage ${details.website_name}.`}
                </p>
                <form
                  className={css.form}
                  onSubmit={(event) => void submit(event)}
                >
                  <div className={css.field}>
                    <label htmlFor="setup-email">Email address</label>
                    <Input
                      id="setup-email"
                      type="email"
                      autoComplete="username"
                      value={details.email}
                      readOnly
                      data-private
                    />
                  </div>
                  <div className={css.field}>
                    <label htmlFor="setup-password">
                      {details.needs_password_setup
                        ? "New password"
                        : "Password"}
                    </label>
                    <div className={css.password}>
                      <Input
                        id="setup-password"
                        type={visible ? "text" : "password"}
                        required
                        maxLength={128}
                        autoComplete={
                          details.needs_password_setup
                            ? "new-password"
                            : "current-password"
                        }
                        value={password}
                        disabled={busy}
                        onChange={(event) => setPassword(event.target.value)}
                        data-private
                      />
                      <Button
                        variant="ghost"
                        className={css.reveal}
                        aria-label={visible ? "Hide password" : "Show password"}
                        aria-pressed={visible}
                        onClick={() => setVisible((value) => !value)}
                      >
                        <span aria-hidden="true">
                          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                        </span>
                      </Button>
                    </div>
                  </div>
                  {details.needs_password_setup && (
                    <div className={css.field}>
                      <label htmlFor="setup-confirm">Confirm password</label>
                      <Input
                        id="setup-confirm"
                        type={visible ? "text" : "password"}
                        required
                        maxLength={128}
                        autoComplete="new-password"
                        value={confirmation}
                        disabled={busy}
                        onChange={(event) =>
                          setConfirmation(event.target.value)
                        }
                        data-private
                      />
                    </div>
                  )}
                  {details.legal_terms_required && (
                    <label className={css.terms}>
                      <input
                        type="checkbox"
                        required
                        checked={accepted}
                        disabled={busy}
                        onChange={(event) => setAccepted(event.target.checked)}
                      />
                      <span>
                        I accept the{" "}
                        <a
                          href="https://cdn.intelliminds.ai/terms-of-service.html"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://cdn.intelliminds.ai/privacy-policy.html"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </label>
                  )}
                  {error && (
                    <p className={css.error} role="alert">
                      {error}
                    </p>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    className={css.submit}
                    disabled={
                      busy ||
                      !password ||
                      (details.needs_password_setup && !confirmation) ||
                      (details.legal_terms_required && !accepted)
                    }
                  >
                    {busy
                      ? "Opening your website…"
                      : details.needs_password_setup
                        ? "Create password and continue"
                        : "Sign in and continue"}
                  </Button>
                  {!details.needs_password_setup && (
                    <a
                      href="/?recover=1"
                      target="_blank"
                      rel="noreferrer"
                      className={css.textButton}
                    >
                      Forgot password?
                    </a>
                  )}
                </form>
              </>
            )
          )}
          {!loading && !details && (
            <>
              <p className={css.error} role="alert">
                {error}
              </p>
              {canRetry && (
                <Button onClick={() => setRetry((value) => value + 1)}>
                  Try again
                </Button>
              )}
              <a href="/" className={css.textButton}>
                Back to sign in
              </a>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
